import { useCallback, useState } from 'react';
import type { MediaDto } from '@gr/core/content/media/dto';
import { useServices } from '../../../app/di';
import { uploadProgress, UploadState, type UploadItem } from './model/uploads';

/**
 * MED-UC-1, MED-UC-1.CC-1, MED-UC-1.CC-2: файлы загружаются по одному (без перегрузки
 * канала), у каждого свой прогресс и результат; ошибка одного не останавливает остальные.
 */
export function useUploads(blogId: string, onUploaded: (media: MediaDto) => void): {
  items: UploadItem[];
  upload(files: File[]): Promise<void>;
  clear(): void;
  busy: boolean;
} {
  const { api } = useServices();
  const [items, setItems] = useState<UploadItem[]>([]);
  const [busy, setBusy] = useState(false);

  const patch = (id: string, change: Partial<UploadItem>): void => setItems((list) => list.map((item) => (item.id === id ? { ...item, ...change } : item)));

  const upload = useCallback(
    async (files: File[]) => {
      const queued = files.map((file, index) => ({ file, id: `${Date.now()}-${index}-${file.name}` }));
      setItems((list) => [...queued.map(({ file, id }) => ({ id, name: file.name, progress: 0, state: UploadState.Uploading, error: null, media: null })), ...list]);
      setBusy(true);
      for (const { file, id } of queued) {
        const result = await api.media.upload(blogId, file, (progress) => patch(id, { progress: uploadProgress(progress.loaded, progress.total) }));
        if (result.success) {
          patch(id, { progress: 1, state: result.data.duplicate ? UploadState.Duplicate : UploadState.Done, media: result.data.media });
          onUploaded(result.data.media);
        } else {
          patch(id, { state: UploadState.Failed, error: result.messages[0] ?? null });
        }
      }
      setBusy(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, blogId, onUploaded],
  );

  return { items, upload, clear: () => setItems((list) => list.filter((item) => item.state === UploadState.Uploading)), busy };
}
